/**
 * @author Agent
 * @description Component for Exceptional Refund at Trip Level
 * 
 * @example
 * <ExceptionalRefundModal
 *   visible={true}
 *   onCancel={() => console.log('Modal closed')}
 *   onApply={(data) => console.log('Refund applied:', data)}
 *   ticketData={[
 *     {
 *       key: '1',
 *       travel: 'VRL',
 *       route: 'Chennai - Bangalore',
 *       seat: 'A1',
 *       pnr: 'PNR123',
 *       passenger: 'John Doe',
 *       journey: '12-12-2021',
 *       range: 'Ordinary',
 *       transaction: 'Online',
 *       agent: 'Agent1',
 *       amount: '₹500',
 *       refund: '₹400 / ₹412 / ₹50'
 *     }
 *   ]}
 *   title="Exceptional Refund"
 *   subtitle="Select Refund Mode AC PM Noc AC Seater/Sleeper Now"
 * />
 * 
 * @param {boolean} visible - Controls modal visibility
 * @param {function} onCancel - Callback when modal is closed
 * @param {function} onApply - Callback when refund is applied with {refundType, refundValue, selectedTickets, selectedRowKeys}
 * @param {array} ticketData - Array of ticket objects with required fields
 * @param {string} title - Modal title (optional)
 * @param {string} subtitle - Modal subtitle (optional)
 */
import React, { useEffect, useState, useRef } from 'react';
import { Modal, Table, Radio, InputNumber, Button, message } from 'antd';
import './exceptional-refund.style.scss';
import { roundNumber } from '../../utils/math.utils';
import { isValid } from '../../utils/utilities';
import { UpiPermissionOtpModal, isUpiOtpRequired } from '../upi-booking/upi-permission-otp.component';
import RefundIcon from './../../images/refundchecklist.png';
import Icon from '@ant-design/icons';


export const ExceptionalRefundModal = (props) => {
    const {
        visible,
        onCancel,
        onApply,
        ticketData = [],
        title = "Exceptional Refund",
        subtitle = "Select Refund Mode AC PM Noc AC Seater/Sleeper Now",
        isExceptionalRefundAllowed = false,
        tripId
    } = props;

    const [refundType, setRefundType] = useState('percentage');
    const [refundValue, setRefundValue] = useState(0);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [updatedTicketData, setUpdatedTicketData] = useState(ticketData);
    const [moreRefundTravellerIds,setMoreRefundTravellerIds] = useState([]);
    // UPI permission OTP gate (REQUIRE_UPI_CANCELLATION_OTP) - verify before applying refund.
    const [upiOtpVisible, setUpiOtpVisible] = useState(false);
    const pendingRefundTicketsRef = useRef(null);

    const resetState = () => {
        setRefundType('percentage');
        setRefundValue(0);
        setSelectedRowKeys([]);
        setMoreRefundTravellerIds([]);
        setUpdatedTicketData(ticketData);
    };

    useEffect(()=>{
        setUpdatedTicketData(ticketData);
    },[ticketData]);

    // The parent may close the modal without going through Exit (e.g. after a
    // successful refund). Start from a clean slate every time it is opened.
    useEffect(()=>{
        if(visible){
            resetState();
        }
    },[visible]);


    const handleRefundValueChange = (value, record) => {
        const updatedData = updatedTicketData.map(ticket => {
            if (ticket.travellerId === record.travellerId) {
                return {
                    ...ticket,
                    refundValue: value
                };
            }
            return ticket;
        });
        setUpdatedTicketData(updatedData);
    };

    const handleMoreRefund = (record) => {
        setMoreRefundTravellerIds([...moreRefundTravellerIds, record.travellerId]);
        // Initialize refundValue with existing refund_value
        setSelectedRowKeys([...selectedRowKeys, record.travellerId]);
        const updatedData = updatedTicketData.map(ticket => {
            if (ticket.travellerId === record.travellerId) {
                return {
                    ...ticket,
                    oldRefundValue: record.refund_value,
                    refundValue: record.refund_value
                };
            }
            return ticket;
        });
        setUpdatedTicketData(updatedData);
    };

    const handleRefundTypeChange = (e) => {
        setRefundType(e.target.value);
        setRefundValue(0);
    };

    const handleApplyRefund = () => {
        if (!selectedRowKeys || selectedRowKeys.length === 0) {
            message.error('Please select at least one ticket');
            return;
        }

        // Use updated ticket data if available (after Apply button was clicked)

        const selectedTickets = updatedTicketData
            .filter(ticket => selectedRowKeys.includes(ticket.travellerId))
            .map(ticket => ({
                pnr: ticket.pnr,
                seat_number: ticket.seat_number,
                travellerId: ticket.travellerId,
                agent_id: ticket.agent_id,
                refundValue: ticket.refundValue,
                oldRefundValue:ticket.oldRefundValue,
                amount_paid: ticket.amount_paid,
                total_gst:ticket.total_gst,
                payment_type:ticket.payment_type,
                ticket_id:ticket.ticket_id
            }));
        
        // Check if refundValue has been calculated for selected tickets
        const hasCalculatedRefund = selectedTickets.some(ticket => ticket.refundValue);
        
        if (!hasCalculatedRefund) {
            message.error('Please click Apply button to calculate refund values first');
            return;
        }

        // When OTP is required, send + verify the OTP first; apply the refund only after.
        if (isUpiOtpRequired()) {
            pendingRefundTicketsRef.current = selectedTickets;
            setUpiOtpVisible(true);
            return;
        }

        onApply(selectedTickets);
    };

    const handleExit = () => {
        resetState();
        onCancel();
    };

    const columns = [
        {
            title: 'Seat#',
            dataIndex: 'seat_number',
            key: 'seat_number',
            render: (text) => <span className="table-text">{text}</span>
        },
        {
            title: 'Name',
            dataIndex: 'full_name',
            key: 'full_name',
            render: (text) => <span className="table-text">{text}</span>
        },
        {
            title: 'PNR',
            dataIndex: 'pnr',
            key: 'pnr',
            render: (text) => <span className="table-text">{text}</span>
        },
        {
            title: 'Phone',
            dataIndex: 'phone_number',
            key: 'phone_number',
            render: (text) => <span className="table-text">{text}</span>
        },
        {
            title: 'Origin',
            dataIndex: 'origin',
            key: 'origin',
            render: (text) => <span className="table-text">{text}</span>
        },
        {
            title: 'Destination',
            dataIndex: 'destination',
            key: 'destination',
            render: (text) => <span className="table-text">{text}</span>
        },
        {
            title: 'Agent',
            dataIndex: 'agent_name',
            key: 'agent_name',
            render: (text) => <span className="table-text">{text}</span>
        },
        {
            title: 'Amount Paid(Fare + GST)',
            dataIndex: 'amount',
            key: 'amount',
            render: (text,row) => <span className="table-text">{row.discounted_fare} + {row.total_gst} = {roundNumber(row.discounted_fare + row.total_gst)}</span>
        },
        {
            title: 'Refund Value',
            dataIndex: 'refundValue',
            key: 'refundValue',
            width: 300,
            render: (value, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {record.refund_value > 0 && moreRefundTravellerIds.indexOf(record.travellerId) == -1 ?  
                    <span className="table-text" style={{width:60}}>
                        {isValid(record.refundValue)?record.refund_value+record.refundValue:record.refund_value}
                        <Icon component={() => (<img src={RefundIcon} style={{marginLeft:2,width:16,height:16}} />)} />
                    </span>
                :
                    <InputNumber
                        value={value || 0}
                        onChange={(val) => handleRefundValueChange(val, record)}
                        min={0}
                        placeholder="Enter refund"
                        className="refund-value-input"
                        prefix="₹"
                        style={{ width: '150px' }}
                    />
                }
                    {record.refund_value > 0 && (
                        <Button 
                            type="primary" 
                            onClick={() => handleMoreRefund(record)}
                            size="small"
                            style={{ 
                                backgroundColor: '#7c3aed',
                                borderColor: '#7c3aed'
                            }}
                        >
                            More Refund
                        </Button>
                    )}
                </div>
            )
        }
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
        getCheckboxProps: (record) => ({
            disabled: record.refund_value > 0 && moreRefundTravellerIds.indexOf(record.travellerId) == -1
        })
    };

    if(!isExceptionalRefundAllowed){
        return null;
    }
    
    return (
        <>
        <Modal
            visible={visible}
            onCancel={handleExit}
            footer={null}
            width={1440}
            className="exceptional-refund-modal"
            centered
        >
            <div className="exceptional-refund-container">
                {/* Header Section */}
                <div className="refund-header">
                    <h2 className="refund-title">{title}</h2>
                    <p className="refund-subtitle">{subtitle}</p>
                </div>

                {/* Refund Type Selection */}
                <div className="refund-type-section">
                    <Radio.Group 
                        onChange={handleRefundTypeChange} 
                        value={refundType}
                        className="refund-type-radio-group"
                    >
                        <Radio value="percentage">% Percentage</Radio>
                        <Radio value="flat">₹ Flat</Radio>
                    </Radio.Group>
                    
                    <div className="refund-value-input-group">
                        <InputNumber
                            value={refundValue}
                            onChange={(value) => setRefundValue(value)}
                            min={0}
                            placeholder={refundType === 'percentage' ? 'Enter %' : 'Enter ₹'}
                            className="refund-value-input"
                            prefix={refundType === 'percentage' ? '%' : '₹'}
                        />
                    </div>
                    <div className='refund-apply-btn-container'>
                        <Button 
                                type="primary"
                                className="apply-refund-btn"
                                onClick={() => {
                                    if (!selectedRowKeys || selectedRowKeys.length === 0) {
                                        message.warning('Please select at least one ticket');
                                        return;
                                    }
                                    
                                    if (!refundValue || refundValue <= 0) {
                                        message.warning('Please enter a valid refund value');
                                        return;
                                    }
                                    
                                    // Calculate refundValue for selected tickets
                                    const updatedData = ticketData.map(ticket => {                                        
                                        if (selectedRowKeys.includes(ticket.travellerId)) {
                                            const totalAmount = roundNumber(ticket.discounted_fare + ticket.total_gst);
                                            let calculatedRefundValue;
                                            
                                            if (refundType === 'percentage') {
                                                calculatedRefundValue = roundNumber((totalAmount * refundValue) / 100);
                                            } else {
                                                calculatedRefundValue = refundValue;
                                            }

                                            if(ticket.refund_value>0){
                                                calculatedRefundValue = ticket.refund_value + calculatedRefundValue;
                                            }
                                            
                                            return {
                                                ...ticket,
                                                refundValue: calculatedRefundValue,
                                                oldRefundValue: ticket.refund_value
                                            };
                                        }
                                        return ticket;
                                    });

                                    setUpdatedTicketData(updatedData);
                                    message.success(`Refund value of ${refundType === 'percentage' ? refundValue + '%' : '₹' + refundValue} applied to ${selectedRowKeys.length} ticket(s)`);
                                }}
                            >
                                Apply
                            </Button>
                    </div>
                </div>

                {/* Table Section */}
                <div className="refund-table-section">
                    <Table
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={updatedTicketData}
                        pagination={false}
                        className="exceptional-refund-table"
                        size="small"
                        rowKey="travellerId"
                    />
                </div>

                {/* Footer Buttons */}
                <div className="refund-footer">
                    <Button 
                        onClick={handleExit} 
                        className="exit-btn"
                        size="large"
                    >
                        Exit
                    </Button>
                    <Button 
                        onClick={handleApplyRefund} 
                        className="refund-btn"
                        type="primary"
                        size="large"
                    >
                        Refund
                    </Button>
                </div>
            </div>
        </Modal>
        <UpiPermissionOtpModal
            visible={upiOtpVisible}
            tripId={tripId}
            onVerified={() => {
                setUpiOtpVisible(false);
                const tickets = pendingRefundTicketsRef.current;
                pendingRefundTicketsRef.current = null;
                if (isValid(tickets)) {
                    onApply(tickets);
                }
            }}
            onCancel={() => {
                setUpiOtpVisible(false);
                pendingRefundTicketsRef.current = null;
            }}
        />
        </>
    );
};

